import { SavedSearchFilters } from "@outschool/gql-backend-generated";
import { gql, useQuery } from "@outschool/ui-apollo";
import React from "react";

import type { CategoryNavLink } from "./navbarCategories";

type NavbarCategorySearchFiltersQueryData = {
  categoryPages?: Array<{
    slug: string;
    searchFilters?: SavedSearchFilters | null;
  } | null> | null;
};

type NavbarCategorySearchFiltersQueryVariables = {
  slugs: string[];
};

export const CATEGORY_NAVBAR_SEARCH_FILTERS_QUERY = gql`
  query CategoryNavbarSearchFilters($slugs: [String!]) {
    categoryPages(slugs: $slugs) {
      slug
      searchFilters {
        adminTags
        age
        capacityMax
        capacityMin
        delivery
        dow
        enabledBooleanFilters
        endBy
        englishProficiencyLevel
        endByTime
        format
        fundingPrograms
        gradeLevel
        hasFilledOutUniqueLearningNeeds
        includeInProgressFixedLengthLiveFormat
        languageOfInstruction
        curriculums
        standards
        multiTermQuery
        order
        pricePerMeetingMax
        pricePerMeetingMin
        q
        startAfter
        startAfterTime
        startBefore
        theme
        time
        timeOfDay
        timeZone
        uniqueLearningNeeds
        userName
        userUid
        includedWithMembership
      }
    }
  }
`;

export function useHydratedNavbarCategories(
  categories: CategoryNavLink[],
  enabled: boolean,
): CategoryNavLink[] {
  const slugs = React.useMemo(
    () => getUniqueCategorySlugs(categories),
    [categories],
  );
  const { data } = useQuery<
    NavbarCategorySearchFiltersQueryData,
    NavbarCategorySearchFiltersQueryVariables
  >(CATEGORY_NAVBAR_SEARCH_FILTERS_QUERY, {
    variables: { slugs },
    skip: !enabled || slugs.length === 0,
  });

  const searchFiltersBySlug = React.useMemo(
    () => buildSearchFiltersBySlug(data?.categoryPages),
    [data?.categoryPages],
  );

  return React.useMemo(() => {
    if (!enabled || searchFiltersBySlug.size === 0) {
      return categories;
    }

    return hydrateNavbarCategories(categories, searchFiltersBySlug);
  }, [categories, enabled, searchFiltersBySlug]);
}

export function getUniqueCategorySlugs(
  categories: CategoryNavLink[],
): string[] {
  const slugs = new Set<string>();

  const visit = (category: CategoryNavLink) => {
    slugs.add(category.slug);
    category.getSubcategories?.().forEach(visit);
  };

  categories.forEach(visit);

  return [...slugs];
}

export function hydrateNavbarCategories(
  categories: CategoryNavLink[],
  searchFiltersBySlug: Map<string, SavedSearchFilters>,
): CategoryNavLink[] {
  return categories.map(category =>
    hydrateNavbarCategory(category, searchFiltersBySlug),
  );
}

function buildSearchFiltersBySlug(
  categoryPages?: NavbarCategorySearchFiltersQueryData["categoryPages"],
): Map<string, SavedSearchFilters> {
  const searchFiltersBySlug = new Map<string, SavedSearchFilters>();

  categoryPages?.forEach(categoryPage => {
    if (categoryPage?.searchFilters) {
      searchFiltersBySlug.set(categoryPage.slug, categoryPage.searchFilters);
    }
  });

  return searchFiltersBySlug;
}

function hydrateNavbarCategory(
  category: CategoryNavLink,
  searchFiltersBySlug: Map<string, SavedSearchFilters>,
): CategoryNavLink {
  const subcategories = category
    .getSubcategories?.()
    .map(subcategory =>
      hydrateNavbarCategory(subcategory, searchFiltersBySlug),
    );

  return {
    ...category,
    ...(searchFiltersBySlug.has(category.slug)
      ? { searchFilters: searchFiltersBySlug.get(category.slug) }
      : {}),
    ...(subcategories ? { getSubcategories: () => subcategories } : {}),
  };
}
